<template>
  <BaseDialog
    class="cfe-dialog full-screen"
    width="960px"
    :is-visible="true"
    :title="$t('mbti.cfe.title')"
    :subtitle="$t('mbti.cfe.subtitle')"
    :notif="$t('mbti.cfe.notif')"
    @close-dialog="$emit('close-dialog', true)"
    ref="baseDialogRef"
  >
    <template #body>
      <div class="wrapper" v-loading="loading">
        <div class="form-row" v-for="(item, key) in feedbackQuestions" :key="item.categoryId">
          <!-- prettier-ignore -->
          <div class="form-category">
            <span class="label">{{ formattedMessage(item.question_ja) }}</span>
            <transition name="fade"><span v-if="hasAnyVote" class="asterisk">＊</span></transition>
          </div>
          <SmileyRating
            v-if="item.id"
            v-model="feedbackQuestions[key].vote"
            :ratingOptionGroup="item.option_group"
          />
        </div>

        <transition name="fade">
          <div v-if="hasAnyVote" class="required-label asterisk">{{ $t('mbti.cfe.requiredLabel') }}</div>
        </transition>
        <div class="form-row" v-if="hasUnsatisfiedRating">
          <div class="form-category input-reason">
            {{ reasonLabel }}<span class="asterisk">＊</span>
          </div>
          <div class="textarea-wrapper">
            <el-input
              type="textarea"
              :rows="2"
              v-model="reason"
              maxlength="1000"
              show-word-limit
              :placeholder="reasonPlaceholder"
            />
            <span class="textarea-sub">{{ $t('mbti.cfe.reasonMaxLength') }}</span>
          </div>
        </div>
      </div>
    </template>

    <template #footer>
      <div
        v-loading="submitLoading"
        :class="['btn-submit', { disabled: !enabledSubmission }]"
        @click="submitFeedback"
      >
        {{ submitLoading ? '' : $t('mbti.cfe.submit') }}
      </div>
    </template>
  </BaseDialog>
</template>

<script>
import { mapState, mapMutations } from 'vuex';
import BaseDialog from '@/components/Base/Dialog';
import SmileyRating from '@/components/Modal/CustomerFeedback/SmileyRating';

import { CUSTOMER_RATING, CUSTOMER_FEEDBACK_Q4 } from '@/utils/constants';
import { getApiMethod } from '@/modules/apiSelector';

export default {
  components: { BaseDialog, SmileyRating },
  data: () => {
    return {
      feedbackQuestions: [],
      reason: '',
      loading: true,
      submitLoading: false,
      reasonPlaceholder: 'お客様へより高い品質のサービスをご提供できるよう、改善に努めますので、低評価ご選択理由について、気になった点や要改善点等出来るだけ具体的なご意見をお聞かせください。',
    };
  },
  watch: {
    hasUnsatisfiedRating() {
      this.$nextTick(() => {
        const scrollRef = this.$refs.baseDialogRef.$refs.scrollBar.$el;
        scrollRef.scrollTop = scrollRef.scrollHeight;
      });
    }
  },
  computed: {
    ...mapState(['lessonId']),
    ...mapState('modal', ['showCfeForm', 'showCfeConfirmation', 'showCfeHideTrainerModal']),
    ...mapState('customerFeedback', ['feedbackRatingOptions']),

    questionWithReason() {
      return this.feedbackQuestions.filter((item) => item.is_detail_required);
    },

    hasUnsatisfiedRating() {
      return this.questionWithReason.some((item) => this.isVoteBelowNormal(item.vote));
    },

    hasAnyVote() {
      return this.feedbackQuestions.some((item) => item.vote);
    },

    voteIsComplete() {
      return this.feedbackQuestions.every((item) => item.vote);
    },

    enabledSubmission() {
      if (this.hasUnsatisfiedRating) {
        return !!this.reason;
      }
      return this.voteIsComplete;
    },

    q4Vote() {
      return this.feedbackQuestions.find((item) => item.id === CUSTOMER_FEEDBACK_Q4)?.vote || null;
    },

    hasVeryBadVote() {
      return this.questionWithReason.some((item) => CUSTOMER_RATING.veryBad === item.vote);
    },

    reasonLabel() {
      switch (this.q4Vote) {
        case CUSTOMER_RATING.veryBad:
          return this.$t('mbti.cfe.q4veryBadReason');
        case CUSTOMER_RATING.bad:
          return this.$t('mbti.cfe.q4badReason');
        default:
          return this.hasVeryBadVote
            ? this.$t('mbti.cfe.q1veryBadReason')
            : this.$t('mbti.cfe.q1badReason');
      }
    }
  },
  methods: {
    ...mapMutations('modal', [
      'setShowCfeConfirmation',
      'setShowCfeForm',
      'setShowCfeHideTrainerModal'
    ]),
    ...mapMutations('customerFeedback', ['setFeedbackRatingOptions']),

    formattedMessage(option) {
      return option.replace(/\\n/g, '\n');
    },

    isVoteBelowNormal(vote) {
      return [CUSTOMER_RATING.veryBad, CUSTOMER_RATING.bad].includes(vote);
    },

    async submitFeedback() {
      if (!this.enabledSubmission) {
        return;
      }

      this.submitLoading = true;

      const filteredForm = this.feedbackQuestions
        .map(({ id, vote }) => ({ question_id: Number(id), vote }))
        .filter((item) => !!item.vote);

      const variables = {
        student_book_id: this.lessonId,
        feedback: filteredForm,
        reason: this.hasUnsatisfiedRating ? this.reason : ''
      };

      const apiMethod = getApiMethod('saveCustomerFeedback');
      await apiMethod(
        variables,
        () => {
          this.submitLoading = false;
          this.setShowCfeForm(false);

          this.isVoteBelowNormal(this.q4Vote)
            ? this.setShowCfeHideTrainerModal(true)
            : this.setShowCfeConfirmation(true);
        },
        (error) => {
          this.submitLoading = false;
          this.$logger.debug('[customer_feedback_answer] Something went wrong: ', error);
        }
      );
    }
  },
  apollo: {
    customer_feedback_questions() {
      const apiMethod = getApiMethod('customerFeedbackQuestions');
      return apiMethod((data) => {
        this.loading = false;
        if (!data) {
          return;
        }
        this.feedbackQuestions = data.questions;
        this.setFeedbackRatingOptions(data.options);
      },
      () => {
        this.loading = false;
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.cfe-dialog ::v-deep {
  .base-dialog {
    margin-top: 3.35vh !important;
    @media screen and (max-width: 959px) and (max-device-width: 959px) and (orientation: landscape) {
      margin-top: 20px !important;
    }
    .subtitle {
      white-space: normal !important;
      @media screen and (max-width: 959px) {
        white-space: pre !important;
      }
    }
  }
  .body-scrollbar {
    max-height: calc(100vh - 163px) !important;
  }
  .wrapper {
    margin-top: 40px;
    min-height: 100px;
    @media screen and (max-width: 498px) {
      padding-bottom: 4px;
    }
    .form-row {
      display: flex;
      width: 100%;
      padding: 12px 0px;
      flex-wrap: wrap;
      box-sizing: border-box;

      &:first-child {
        @media screen and (max-width: 959px) {
          border-top: 1px solid #ebeef1;
        }
      }

      @media screen and (max-width: 959px) {
        border-bottom: 1px solid #ebeef1;
        margin-bottom: 0px;
      }

      .form-category {
        min-width: 184px;
        width: 184px;
        color: #0087d2;
        font-size: 14px;
        font-weight: 700;
        line-height: 21px;
        margin-right: 16px;
        .label {
          white-space: pre-line;
        }
        @media screen and (max-width: 959px) {
          flex-basis: 100%;
          line-height: 21px;
          margin-bottom: 8px;
          .label {
            white-space: nowrap;
          }
          &.input-reason .label {
            white-space: pre-line;
          }
        }
      }
      .textarea-wrapper {
        flex: 1;
        position: relative;
        .textarea-sub {
          position: absolute;
          font-size: 10px;
          line-height: 10px;
          color: #d1d4d7;
          left: 0px;
          bottom: 0px;
        }
      }
      .el-textarea {
        height: 78px;
        @media screen and (max-width: 959px) {
          height: 142px;
        }
        .el-input__count {
          font-size: 10px;
          color: #d1d4d7;
          bottom: -2px;
          right: 0px;
        }
      }
      .el-textarea__inner {
        font-family: 'NotoSansJP';
        font-size: 14px;
        line-height: 21px;
        font-weight: 500;
        height: 64px;
        color: #141821;
        padding: 8px 12px;
        border-radius: 5px;
        border: 1px solid #d1d4d7;
        resize: none;
        @media screen and (max-width: 959px) {
          height: 128px;
        }
      }
    }
    .asterisk {
      color: #ff0000;
      font-weight: 700;
      font-size: 14px;
      &.required-label {
        @media screen and (max-width: 959px) {
          margin-top: 8px;
        }
      }
    }
  }
  .btn-submit {
    display: flex;
    border-radius: 8px;
    background: #0087d2;
    width: 240px;
    max-width: 382px;
    padding: 2.4vh 0;
    box-sizing: border-box;
    min-height: 7.6vh;
    justify-content: center;
    align-items: center;
    font-size: 14px;
    font-weight: 500;
    color: #ffffff;
    cursor: pointer;
    user-select: none;
    @media screen and (max-width: 498px) {
      min-height: 56px;
    }
    &.disabled {
      background: #d1d4d7;
    }
    .el-loading-mask {
      background-color: rgba(255, 255, 255, 0.3) !important;
    }
    .el-loading-spinner {
      .circular {
        width: 38px;
        height: 38px;
      }
      .path {
        stroke: #fff;
      }
    }
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
